import { FormattedMessage } from 'react-intl';
import { ComponentProps, Dispatch, SetStateAction } from 'react';
import type { userAgent } from 'next/server';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type CountryCode from '@alltrails/core/types/CountryCode';
import SectionDivider from '@alltrails/denali/components/SectionDivider';
import { useSelector } from 'react-redux';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type Coupon from '@alltrails/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import classNames from 'classnames';
import type { CardElementChangeEvent } from '../../../types/ReactRecurly';
import { CARD_VALIDATION_NAMES, TokenValidationErrors } from '../../../types/RecurlyTokenization';
import TokenizedCreditCardForm from '../../TokenizedCreditCardForm';
import type ProResponse from '../../../types/ProResponse';
import ApplePayButton from '../ApplePayButton';
import ErrorText from '../ErrorText';
import PayPalButton from '../PayPalButton';
import GooglePayButton from '../GooglePayButton';
import LoadingOverlay from '../LoadingOverlay';
import useIosWebCheckoutExperiment from '../../../hooks/useIosWebCheckoutExperiment';
import styles from './RadioPaymentForm.module.scss';
import PaymentOptions, { PaymentOptionValues } from './PaymentOptions';

type Props = {
  billingCountry: CountryCode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  errors?: ProResponse['errors'];
  isGooglePayAvailable: boolean;
  isUpgrade?: boolean;
  onApplePayToken: ComponentProps<typeof ApplePayButton>['onApplePayToken'];
  onBillingCountryChange: (countryCode: CountryCode, arg1: any) => void;
  onGooglePayToken: ComponentProps<typeof GooglePayButton>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PayPalButton>['onPayPalToken'];
  plan: Plan;
  selectedPaymentType?: PaymentOptionValues;
  setClientValidationErrors: (inputName: CARD_VALIDATION_NAMES, value: string | CardElementChangeEvent) => void;
  setErrors: Dispatch<SetStateAction<ProResponse['errors'] | undefined>>;
  setSelectedPaymentType: Dispatch<SetStateAction<PaymentOptionValues | undefined>>;
  submitting: boolean;
  validationErrors: TokenValidationErrors;
  purchaseTier: PurchaseTier;
};

export default function RadioPaymentForm({
  billingCountry,
  currencyCode,
  errors,
  isGooglePayAvailable,
  isUpgrade,
  onBillingCountryChange,
  purchaseTier,
  selectedPaymentType,
  setClientValidationErrors,
  setErrors,
  setSelectedPaymentType,
  submitting,
  validationErrors
}: Props) {
  const browserName = useSelector((state: { browser: ReturnType<typeof userAgent>['browser'] }) => state.browser.name);
  const isMobile = useIsMobileSizedScreen();
  const { isIosWebCheckoutOptimizationEnabled } = useIosWebCheckoutExperiment();

  const handleChange = (newPaymentType: PaymentOptionValues) => {
    setSelectedPaymentType(newPaymentType);
  };

  return (
    <>
      <div className={classNames(styles.container, { [styles.iosWebCheckoutOptimizationEnabled]: isIosWebCheckoutOptimizationEnabled })}>
        <SectionDivider classes={styles.topRadioFormDivider} />
        <PaymentOptions
          isMobile={isMobile}
          currencyCode={currencyCode}
          browserName={browserName}
          selectedPaymentType={selectedPaymentType}
          handleChange={handleChange}
          isGooglePayAvailable={isGooglePayAvailable}
          isIosWebCheckoutOptimizationEnabled={isIosWebCheckoutOptimizationEnabled}
        />
      </div>
      {/* Hide the card details divider if the iOS web checkout optimization is enabled */}
      {!isIosWebCheckoutOptimizationEnabled && (
        <div>
          <SectionDivider>
            {selectedPaymentType === PaymentOptionValues.CreditCard ? <FormattedMessage defaultMessage="Enter your card details" /> : null}
          </SectionDivider>
        </div>
      )}
      {selectedPaymentType === PaymentOptionValues.CreditCard && (
        <TokenizedCreditCardForm
          validationErrors={validationErrors}
          handleInputChange={(input, value: string | unknown) => {
            if (errors) {
              setErrors(undefined);
            }

            setClientValidationErrors(input, value as string | CardElementChangeEvent);
          }}
          billingCountry={{
            onChange: onBillingCountryChange,
            value: billingCountry
          }}
          isUpgrade={isUpgrade}
          purchaseTier={purchaseTier}
        />
      )}
      {errors && <ErrorText errors={errors} />}
      {submitting && <LoadingOverlay />}
    </>
  );
}
